// src/auth/use-recovery.ts
//
// React-query mutation hooks for the login-recovery suite, mirroring the app's
// service-hook convention (see src/documents/services/use-upload-document.ts):
// one `useMutation` per Identity call, thin wrappers over `recovery-service.ts`.
// These are unauthenticated one-shot commands (no cache to invalidate), so they
// stay mutations rather than queries.
import { useMutation } from '@tanstack/react-query';
import {
  forgotPassword,
  loginByCode,
  setPassword,
  type LoginByCodeResult,
} from './recovery-service';

/** POST /api/Account/ForgotPassword — request the reset access-code email. */
export function useForgotPassword() {
  return useMutation<void, Error, { email: string }>({
    mutationFn: ({ email }) => forgotPassword(email),
  });
}

/** POST /api/Account/LoginByCode — verify the access code, mint the reset token. */
export function useLoginByCode() {
  return useMutation<LoginByCodeResult, Error, { email: string; code: string }>({
    mutationFn: ({ email, code }) => loginByCode(email, code),
  });
}

/** POST /api/Account/SetPassword — commit the new password against the token. */
export function useSetPassword() {
  return useMutation<void, Error, { email: string; password: string; passwordResetToken: string }>({
    mutationFn: (params) => setPassword(params),
  });
}
